import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { createSelector } from 'reselect';
import { axiosInstance } from '../routes/axiosInstance';

// Thunks
// Fetch overall performance data (example endpoint: /market/performance)
export const fetchRanksPerformance = createAsyncThunk(
  'reports/fetchPerformance',
  async (_, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.get('/market/users-rank-performance');
      return Array.isArray(res.data) ? res.data : [];
    } catch (err) {
      return rejectWithValue(err?.response?.data || err.message || 'Fetch failed');
    }
  }
);

// Generic fetch for an export/report by type (keeps options open for CSV/XLS export endpoints)
export const fetchReportByType = createAsyncThunk(
  'reports/fetchReportByType',
  async ({ reportType, params = {} }, { rejectWithValue }) => {
    try {
      // Example: /reports/<reportType>?...
      const res = await axiosInstance.get(`/reports/${reportType}`, { params });
      return { reportType, data: res.data };
    } catch (err) {
      return rejectWithValue(err?.response?.data || err.message || `Failed to fetch ${reportType}`);
    }
  }
);

// GET one monthly-stats record. Only include the user param if provided
// (admins can query any user).
const requestMonthlyStats = async ({ userId, resourceId, year, month, track, horizonOffset }) => {
  const params = { resource: resourceId, year, month, track };
  if (typeof horizonOffset === 'number') params.horizon_offset = horizonOffset;
  if (userId) params.user = userId;

  const res = await axiosInstance.get('/market/report/monthly-stats/list', { params });
  return res.data?.data || res.data;
};

// Fetch monthly statistics for a specific user/resource/month/track
export const fetchMonthlyStats = createAsyncThunk(
  'reports/fetchMonthlyStats',
  async (args, { rejectWithValue }) => {
    try {
      return await requestMonthlyStats(args);
    } catch (err) {
      return rejectWithValue(err?.response?.data || err.message || 'Failed to fetch monthly stats');
    }
  }
);

// Same payload as fetchMonthlyStats, but stored in a separate slot so the
// leaderboard can compute month-over-month deltas without clobbering the
// current-month data.
export const fetchMonthlyStatsPrevious = createAsyncThunk(
  'reports/fetchMonthlyStatsPrevious',
  async (args, { rejectWithValue }) => {
    try {
      return await requestMonthlyStats(args);
    } catch (err) {
      return rejectWithValue(err?.response?.data || err.message || 'Failed to fetch previous month stats');
    }
  }
);

// Same payload again, in its own slot for the league-qualification card. That
// card always asks for one fixed track, so sharing monthlyStatsData let its
// response overwrite the leaderboard's data for the selected track.
export const fetchQualificationStats = createAsyncThunk(
  'reports/fetchQualificationStats',
  async (args, { rejectWithValue }) => {
    try {
      return await requestMonthlyStats(args);
    } catch (err) {
      return rejectWithValue(err?.response?.data || err.message || 'Failed to fetch qualification stats');
    }
  }
);

// Fetch per-horizon monthly overview for a specific user/resource/month/track
export const fetchHorizonOverview = createAsyncThunk(
  'reports/fetchHorizonOverview',
  async ({ userId, resourceId, year, month, track }, { rejectWithValue }) => {
    try {
      const params = {
        resource: resourceId,
        year,
        month,
        track,
      };

      if (userId) {
        params.user = userId;
      }

      const res = await axiosInstance.get('/market/report/monthly-stats/overview', { params });
      return res.data?.data || res.data || [];
    } catch (err) {
      return rejectWithValue(err?.response?.data || err.message || 'Failed to fetch horizon overview');
    }
  }
);

// Fetch per-gate-closure breakdown for a specific horizon
export const fetchGateClosureOverview = createAsyncThunk(
  'reports/fetchGateClosureOverview',
  async ({ userId, resourceId, year, month, track, horizonOffset }, { rejectWithValue }) => {
    try {
      const params = {
        resource: resourceId,
        year,
        month,
        track,
        horizon_offset: horizonOffset,
      };

      if (userId) {
        params.user = userId;
      }

      const res = await axiosInstance.get('/market/report/monthly-stats/overview', { params });
      return res.data?.data || res.data || [];
    } catch (err) {
      return rejectWithValue(err?.response?.data || err.message || 'Failed to fetch gate closure overview');
    }
  }
);

// Fetch full monthly stats for a specific gate closure hour (intraday drill-down)
export const fetchGateHourDetail = createAsyncThunk(
  'reports/fetchGateHourDetail',
  async ({ userId, resourceId, year, month, track, horizonOffset, gateClosureHour }, { rejectWithValue }) => {
    try {
      const params = {
        resource: resourceId,
        year,
        month,
        track,
        horizon_offset: horizonOffset,
        gate_closure_hour: gateClosureHour,
      };

      if (userId) {
        params.user = userId;
      }

      const res = await axiosInstance.get('/market/report/monthly-stats/list', { params });
      return res.data?.data || res.data;
    } catch (err) {
      return rejectWithValue(err?.response?.data || err.message || 'Failed to fetch gate hour detail');
    }
  }
);

// Fetch active users who have submitted forecasts for a specific period
export const fetchActiveUsers = createAsyncThunk(
  'reports/fetchActiveUsers',
  async ({ resourceId, year, month, track }, { rejectWithValue }) => {
    try {
      const params = {
        resource: resourceId,
        year,
        month,
        track,
      };

      const res = await axiosInstance.get('/market/report/monthly-stats/active-users/', { params });

      return res.data?.data || res.data;
    } catch (err) {
      return rejectWithValue({
        data: err?.response?.data,
        status: err?.response?.status,
        message: err.message || 'Failed to fetch active users'
      });
    }
  }
);

// Fetch month-over-month comparison statistics
export const fetchMonthOverMonthStats = createAsyncThunk(
  'reports/fetchMonthOverMonthStats',
  async ({ userId, resourceId, year, month, track, horizonOffset }, { rejectWithValue }) => {
    try {
      const params = {
        resource: resourceId,
        year,
        month,
        track,
      };

      // Scope the comparison to the selected horizon so both months are
      // measured on the same track. Without it the backend picks an arbitrary
      // horizon row, producing a cross-horizon (e.g. Intraday vs D+1) delta.
      if (horizonOffset != null) {
        params.horizon_offset = horizonOffset;
      }

      // Only include user param if provided (for admin queries)
      if (userId) {
        params.user = userId;
      }

      const res = await axiosInstance.get('/market/report/monthly-stats/month-over-month/', { params });

      return res.data?.data || res.data;
    } catch (err) {
      return rejectWithValue({
        data: err?.response?.data,
        status: err?.response?.status,
        message: err.message || 'Failed to fetch month-over-month stats'
      });
    }
  }
);

// Fetch slot matrix + daily score row for a single (forecaster, resource,
// target_day) — powers the per-day drill-down drawer on the Analytics page.
// Both endpoints are scoped server-side: forecasters only see their own rows.
export const fetchDayDetail = createAsyncThunk(
  'reports/fetchDayDetail',
  async ({ resourceId, userId, targetDay, horizonOffset }, { rejectWithValue }) => {
    try {
      const baseParams = {
        resource: resourceId,
        start_date: targetDay,
        end_date: targetDay,
      };
      if (userId) baseParams.user = userId;

      const scoreParams = { ...baseParams };
      const matrixParams = { ...baseParams, variable: 'q50' };
      if (typeof horizonOffset === 'number') {
        scoreParams.horizon_offset = horizonOffset;
        // The slot matrix is now keyed per (target_day, horizon_offset) too —
        // intraday is the 24-slot grid, D+N is a 1-slot best-cover matrix.
        // Sending horizon_offset prevents the legacy collision where the
        // intraday matrix was returned for D+N drill-downs.
        matrixParams.horizon_offset = horizonOffset;
      }

      // Rule 7 detail (challenges opened, submission status, peer-Q75 fill)
      // is only meaningful for D+N. Skip the call for intraday so we don't
      // pay an extra round-trip when it has no payload.
      const detailParams = typeof horizonOffset === 'number' && horizonOffset >= 1
        ? { ...baseParams, target_day: targetDay, horizon_offset: horizonOffset }
        : null;
      // ``baseParams`` already carries ``start_date`` / ``end_date`` =
      // targetDay; ``forecaster-day-detail`` wants a single ``target_day``
      // instead, so strip the duplicates.
      if (detailParams) {
        delete detailParams.start_date;
        delete detailParams.end_date;
      }

      const [scoreRes, matrixRes, detailRes] = await Promise.all([
        axiosInstance.get('/market/forecaster-daily-scores/list', { params: scoreParams }),
        axiosInstance.get('/market/forecaster-slot-matrix/list', { params: matrixParams }),
        detailParams
          ? axiosInstance.get('/market/forecaster-day-detail', { params: detailParams })
          : Promise.resolve(null),
      ]);

      const scoreRows = Array.isArray(scoreRes.data?.data) ? scoreRes.data.data
        : (Array.isArray(scoreRes.data) ? scoreRes.data : []);
      const matrixRows = Array.isArray(matrixRes.data?.data) ? matrixRes.data.data
        : (Array.isArray(matrixRes.data) ? matrixRes.data : []);
      const dnDetail = detailRes
        ? (detailRes.data?.data ?? detailRes.data ?? null)
        : null;

      return { targetDay, horizonOffset, scoreRows, matrixRows, dnDetail };
    } catch (err) {
      return rejectWithValue(err?.response?.data || err.message || 'Failed to fetch day detail');
    }
  }
);

// Fetch submission heatmap for a specific resource, month, and (optionally)
// horizon. When `horizonOffset` is omitted/null the backend returns all
// sessions for the resource and period regardless of horizon (useful for the
// compliance empty-state where the user's submitted-horizon distribution is
// unknown). `userId` is optional — when provided (and the caller is a
// resource manager or admin) the backend returns the heatmap for that user
// instead of the authenticated one.
export const fetchSubmissionHeatmap = createAsyncThunk(
  'reports/fetchSubmissionHeatmap',
  async ({ resourceId, year, month, horizonOffset, userId }, { rejectWithValue }) => {
    try {
      const params = {
        resource: resourceId,
        year,
        month,
      };
      if (horizonOffset != null) params.horizon_offset = horizonOffset;
      if (userId) params.user = userId;

      const res = await axiosInstance.get('/market/report/submission-heatmap', { params });
      return res.data?.data || res.data;
    } catch (err) {
      return rejectWithValue(err?.response?.data || err.message || 'Failed to fetch submission heatmap');
    }
  }
);

// Fetch the bucketed (day/week/month) forecaster leaderboard series — powers
// the Trends tab's leaderboard matrix and a single forecaster's trajectory.
// Server-side: anonymised peers (username); resource-scoped for forecasters.
export const fetchLeaderboardSeries = createAsyncThunk(
  'reports/fetchLeaderboardSeries',
  async ({ resourceId, track, grain, startDate, endDate, horizonOffset, topN, userId, ensembleModel }, { rejectWithValue }) => {
    try {
      const params = {
        resource: resourceId,
        track,
        grain,
        start_date: startDate,
        end_date: endDate,
        horizon_offset: horizonOffset,
      };
      if (topN != null) params.top_n = topN;
      if (userId) params.user = userId;
      if (ensembleModel) params.ensemble_model = ensembleModel;

      const res = await axiosInstance.get('/market/forecaster-leaderboard/series', { params });
      return res.data?.data || res.data;
    } catch (err) {
      return rejectWithValue(err?.response?.data || err.message || 'Failed to fetch leaderboard series');
    }
  }
);

// Fetch the market-health series (ensemble vs best-individual vs top-N) for the
// Trends tab. Owner/admin only — forecasters are denied server-side.
export const fetchMarketHealthSeries = createAsyncThunk(
  'reports/fetchMarketHealthSeries',
  async ({ resourceId, track, grain, startDate, endDate, horizonOffset, topN, ensembleModel }, { rejectWithValue }) => {
    try {
      const params = {
        resource: resourceId,
        track,
        grain,
        start_date: startDate,
        end_date: endDate,
        horizon_offset: horizonOffset,
      };
      if (topN != null) params.top_n = topN;
      if (ensembleModel) params.ensemble_model = ensembleModel;

      const res = await axiosInstance.get('/market/market-health/series', { params });
      return res.data?.data || res.data;
    } catch (err) {
      return rejectWithValue({
        data: err?.response?.data,
        status: err?.response?.status,
        message: err.message || 'Failed to fetch market health series',
      });
    }
  }
);

// Fetch the pooled error-by-leadtime series (forecasters vs ensemble) for the
// Overview tab. Owner/admin only; forecasters are denied server-side.
export const fetchLeadtimeSeries = createAsyncThunk(
  'reports/fetchLeadtimeSeries',
  async ({ resourceId, track, startDate, endDate, horizonOffset, ensembleModel }, { rejectWithValue }) => {
    try {
      const params = {
        resource: resourceId,
        track,
        start_date: startDate,
        end_date: endDate,
        horizon_offset: horizonOffset,
      };
      if (ensembleModel) params.model = ensembleModel;

      const res = await axiosInstance.get('/market/leadtime-scores/series', { params });
      return res.data?.data || res.data;
    } catch (err) {
      return rejectWithValue(err?.response?.data || err.message || 'Failed to fetch leadtime series');
    }
  }
);

// Initial state - keep it minimal for now and extend as needed
const initialState = {
  perfData: [], // raw performance rows from backend
  metrics: [], // unique metric keys found in perfData
  sessions: [], // unique session ids (sorted)
  selectedMetric: null,
  selectedWindow: 50, // last N sessions (0 = all)
  loading: false,
  error: null,
  // Monthly stats state
  // The *RequestId fields hold the latest request per slot; responses from
  // older requests (e.g. the previous market after a switch) are dropped.
  monthlyStatsData: null, // stores API response data
  monthlyStatsLoading: false,
  monthlyStatsError: null,
  monthlyStatsRequestId: null,
  // Previous-month stats (same shape as monthlyStatsData, used for MoM deltas)
  monthlyStatsPreviousData: null,
  monthlyStatsPreviousLoading: false,
  monthlyStatsPreviousError: null,
  monthlyStatsPreviousRequestId: null,
  // League-qualification card stats (same shape, fixed track)
  qualificationStatsData: null,
  qualificationStatsLoading: false,
  qualificationStatsError: null,
  qualificationStatsRequestId: null,
  // Active users state
  activeUsers: [],
  activeUsersLoading: false,
  activeUsersError: null,
  // Month-over-month stats state
  monthOverMonthData: null,
  monthOverMonthLoading: false,
  monthOverMonthError: null,
  // Horizon overview state
  horizonOverviewData: [],
  horizonOverviewLoading: false,
  horizonOverviewError: null,
  selectedReportHorizon: null,
  // Gate closure overview state (per-gate breakdown for a selected horizon)
  gateClosureData: [],
  gateClosureLoading: false,
  gateClosureError: null,
  // Intraday gate hour drill-down state
  gateHourDetailData: null,
  gateHourDetailLoading: false,
  gateHourDetailError: null,
  selectedGateHour: null,
  // Submission heatmap state
  submissionHeatmapData: null,
  submissionHeatmapLoading: false,
  submissionHeatmapError: null,
  // Trends tab — leaderboard series (matrix + subject trajectory)
  leaderboardSeriesData: null,
  leaderboardSeriesLoading: false,
  leaderboardSeriesError: null,
  // Trends tab — market-health series (ensemble vs best vs top-N; owner/admin)
  marketHealthSeriesData: null,
  marketHealthSeriesLoading: false,
  marketHealthSeriesError: null,
  // Overview tab — error-by-leadtime series (forecasters vs ensemble)
  leadtimeSeriesData: null,
  leadtimeSeriesLoading: false,
  leadtimeSeriesError: null,
  // Per-day drill-down drawer state
  dayDetail: {
    open: false,
    targetDay: null,
    userId: null,
    resourceId: null,
    horizonOffset: null,
    metric: null,
    track: null,
    scoreRows: [],
    matrixRows: [],
    // D+N Rule 7 detail — challenges opened for the slice, the forecaster's
    // per-challenge submission status, peer-Q75 substitute values. Null for
    // intraday (no Rule 7 there) and during initial fetch.
    dnDetail: null,
    loading: false,
    error: null,
  },
};

const reportsSlice = createSlice({
  name: 'reports',
  initialState,
  reducers: {
    setSelectedMetric(state, action) {
      state.selectedMetric = action.payload;
    },
    setSelectedWindow(state, action) {
      state.selectedWindow = action.payload;
    },
    setPerfData(state, action) {
      state.perfData = action.payload || [];
      // derive metrics and sessions
      const metricsSet = new Set();
      const sessionsSet = new Set();
      (state.perfData || []).forEach((r) => {
        if (r && r.metric) metricsSet.add(r.metric);
        if (r && typeof r.session_id !== 'undefined') sessionsSet.add(r.session_id);
      });
      state.metrics = Array.from(metricsSet).sort();
      state.sessions = Array.from(sessionsSet).sort((a, b) => a - b);
      // ensure selectedMetric is valid
      if (!state.selectedMetric && state.metrics.length) state.selectedMetric = state.metrics[0];
    },
    clearReportsState(state) {
      state.perfData = [];
      state.metrics = [];
      state.sessions = [];
      state.selectedMetric = null;
      state.loading = false;
      state.error = null;
    },
    clearMonthlyStats(state) {
      state.monthlyStatsData = null;
      state.monthlyStatsLoading = false;
      state.monthlyStatsError = null;
      state.monthlyStatsRequestId = null;
    },
    clearMonthOverMonthStats(state) {
      state.monthOverMonthData = null;
      state.monthOverMonthLoading = false;
      state.monthOverMonthError = null;
    },
    setSelectedReportHorizon(state, action) {
      state.selectedReportHorizon = action.payload;
    },
    clearHorizonOverview(state) {
      state.horizonOverviewData = [];
      state.horizonOverviewLoading = false;
      state.horizonOverviewError = null;
      state.selectedReportHorizon = null;
      state.gateClosureData = [];
      state.gateClosureLoading = false;
      state.gateClosureError = null;
      state.gateHourDetailData = null;
      state.gateHourDetailLoading = false;
      state.gateHourDetailError = null;
      state.selectedGateHour = null;
    },
    setSelectedGateHour(state, action) {
      state.selectedGateHour = action.payload;
    },
    clearGateHourDetail(state) {
      state.gateHourDetailData = null;
      state.gateHourDetailLoading = false;
      state.gateHourDetailError = null;
      state.selectedGateHour = null;
    },
    clearSubmissionHeatmap(state) {
      state.submissionHeatmapData = null;
      state.submissionHeatmapLoading = false;
      state.submissionHeatmapError = null;
    },
    openDayDetail(state, action) {
      const { targetDay, userId, resourceId, horizonOffset, metric, track } = action.payload || {};
      state.dayDetail.open = true;
      state.dayDetail.targetDay = targetDay || null;
      state.dayDetail.userId = userId || null;
      state.dayDetail.resourceId = resourceId || null;
      state.dayDetail.horizonOffset = typeof horizonOffset === 'number' ? horizonOffset : null;
      state.dayDetail.metric = metric || null;
      state.dayDetail.track = track || null;
      state.dayDetail.scoreRows = [];
      state.dayDetail.matrixRows = [];
      state.dayDetail.dnDetail = null;
      state.dayDetail.error = null;
    },
    closeDayDetail(state) {
      state.dayDetail.open = false;
      state.dayDetail.targetDay = null;
      state.dayDetail.scoreRows = [];
      state.dayDetail.matrixRows = [];
      state.dayDetail.dnDetail = null;
      state.dayDetail.loading = false;
      state.dayDetail.error = null;
    },
    setDayDetailHorizon(state, action) {
      // Pivot the open drawer to a different horizon for the same day.
      // The fetch effect picks up the change and reloads the score row.
      state.dayDetail.horizonOffset = typeof action.payload === 'number' ? action.payload : null;
    },
    setDayDetailTargetDay(state, action) {
      // Pivot the open drawer to a different day for the same (resource, user,
      // horizon). The fetch effect picks up the change and reloads.
      const day = typeof action.payload === 'string' ? action.payload : null;
      if (!day || day === state.dayDetail.targetDay) return;
      state.dayDetail.targetDay = day;
      state.dayDetail.scoreRows = [];
      state.dayDetail.matrixRows = [];
      state.dayDetail.dnDetail = null;
      state.dayDetail.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchRanksPerformance.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchRanksPerformance.fulfilled, (state, action) => {
        state.loading = false;
        state.perfData = action.payload || [];
        // derive metrics and sessions
        const metricsSet = new Set();
        const sessionsSet = new Set();
        (state.perfData || []).forEach((r) => {
          if (r && r.metric) metricsSet.add(r.metric);
          if (r && typeof r.session_id !== 'undefined') sessionsSet.add(r.session_id);
        });
        state.metrics = Array.from(metricsSet).sort();
        state.sessions = Array.from(sessionsSet).sort((a, b) => a - b);
        if (!state.selectedMetric && state.metrics.length) state.selectedMetric = state.metrics[0];
      })
      .addCase(fetchRanksPerformance.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || action.error?.message || 'Failed to fetch performance data';
      })
      .addCase(fetchReportByType.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchReportByType.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(fetchReportByType.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || action.error?.message || 'Report fetch failed';
      })
      .addCase(fetchMonthlyStats.pending, (state, action) => {
        state.monthlyStatsLoading = true;
        state.monthlyStatsError = null;
        state.monthlyStatsRequestId = action.meta.requestId;
      })
      .addCase(fetchMonthlyStats.fulfilled, (state, action) => {
        if (action.meta.requestId !== state.monthlyStatsRequestId) return;
        state.monthlyStatsLoading = false;
        state.monthlyStatsData = action.payload;
        state.monthlyStatsError = null;
      })
      .addCase(fetchMonthlyStats.rejected, (state, action) => {
        if (action.meta.requestId !== state.monthlyStatsRequestId) return;
        state.monthlyStatsLoading = false;
        state.monthlyStatsError = action.payload || action.error?.message || 'Failed to fetch monthly stats';
      })
      .addCase(fetchMonthlyStatsPrevious.pending, (state, action) => {
        state.monthlyStatsPreviousLoading = true;
        state.monthlyStatsPreviousError = null;
        state.monthlyStatsPreviousRequestId = action.meta.requestId;
      })
      .addCase(fetchMonthlyStatsPrevious.fulfilled, (state, action) => {
        if (action.meta.requestId !== state.monthlyStatsPreviousRequestId) return;
        state.monthlyStatsPreviousLoading = false;
        state.monthlyStatsPreviousData = action.payload;
        state.monthlyStatsPreviousError = null;
      })
      .addCase(fetchMonthlyStatsPrevious.rejected, (state, action) => {
        if (action.meta.requestId !== state.monthlyStatsPreviousRequestId) return;
        state.monthlyStatsPreviousLoading = false;
        state.monthlyStatsPreviousData = null;
        state.monthlyStatsPreviousError = action.payload || action.error?.message || 'Failed to fetch previous month stats';
      })
      .addCase(fetchQualificationStats.pending, (state, action) => {
        state.qualificationStatsLoading = true;
        state.qualificationStatsError = null;
        state.qualificationStatsRequestId = action.meta.requestId;
      })
      .addCase(fetchQualificationStats.fulfilled, (state, action) => {
        if (action.meta.requestId !== state.qualificationStatsRequestId) return;
        state.qualificationStatsLoading = false;
        state.qualificationStatsData = action.payload;
        state.qualificationStatsError = null;
      })
      .addCase(fetchQualificationStats.rejected, (state, action) => {
        if (action.meta.requestId !== state.qualificationStatsRequestId) return;
        state.qualificationStatsLoading = false;
        state.qualificationStatsError = action.payload || action.error?.message || 'Failed to fetch qualification stats';
      })
      .addCase(fetchActiveUsers.pending, (state) => {
        state.activeUsersLoading = true;
        state.activeUsersError = null;
      })
      .addCase(fetchActiveUsers.fulfilled, (state, action) => {
        state.activeUsersLoading = false;
        state.activeUsers = action.payload;
      })
      .addCase(fetchActiveUsers.rejected, (state, action) => {
        state.activeUsersLoading = false;
        state.activeUsersError = action.payload;
      })
      .addCase(fetchMonthOverMonthStats.pending, (state) => {
        state.monthOverMonthLoading = true;
        state.monthOverMonthError = null;
      })
      .addCase(fetchMonthOverMonthStats.fulfilled, (state, action) => {
        state.monthOverMonthLoading = false;
        state.monthOverMonthData = action.payload;
        state.monthOverMonthError = null;
      })
      .addCase(fetchMonthOverMonthStats.rejected, (state, action) => {
        state.monthOverMonthLoading = false;
        state.monthOverMonthError = action.payload;
      })
      .addCase(fetchHorizonOverview.pending, (state) => {
        state.horizonOverviewLoading = true;
        state.horizonOverviewError = null;
      })
      .addCase(fetchHorizonOverview.fulfilled, (state, action) => {
        state.horizonOverviewLoading = false;
        state.horizonOverviewData = Array.isArray(action.payload) ? action.payload : [];
      })
      .addCase(fetchHorizonOverview.rejected, (state, action) => {
        state.horizonOverviewLoading = false;
        state.horizonOverviewError = action.payload || action.error?.message || 'Failed to fetch horizon overview';
      })
      .addCase(fetchGateClosureOverview.pending, (state) => {
        state.gateClosureLoading = true;
        state.gateClosureError = null;
      })
      .addCase(fetchGateClosureOverview.fulfilled, (state, action) => {
        state.gateClosureLoading = false;
        state.gateClosureData = Array.isArray(action.payload) ? action.payload : [];
      })
      .addCase(fetchGateClosureOverview.rejected, (state, action) => {
        state.gateClosureLoading = false;
        state.gateClosureError = action.payload || action.error?.message || 'Failed to fetch gate closure overview';
      })
      .addCase(fetchGateHourDetail.pending, (state) => {
        state.gateHourDetailLoading = true;
        state.gateHourDetailError = null;
      })
      .addCase(fetchGateHourDetail.fulfilled, (state, action) => {
        state.gateHourDetailLoading = false;
        state.gateHourDetailData = action.payload;
        state.gateHourDetailError = null;
      })
      .addCase(fetchGateHourDetail.rejected, (state, action) => {
        state.gateHourDetailLoading = false;
        state.gateHourDetailError = action.payload || action.error?.message || 'Failed to fetch gate hour detail';
      })
      .addCase(fetchSubmissionHeatmap.pending, (state) => {
        state.submissionHeatmapLoading = true;
        state.submissionHeatmapError = null;
      })
      .addCase(fetchSubmissionHeatmap.fulfilled, (state, action) => {
        state.submissionHeatmapLoading = false;
        state.submissionHeatmapData = action.payload;
        state.submissionHeatmapError = null;
      })
      .addCase(fetchSubmissionHeatmap.rejected, (state, action) => {
        state.submissionHeatmapLoading = false;
        state.submissionHeatmapError = action.payload || action.error?.message || 'Failed to fetch submission heatmap';
      })
      .addCase(fetchDayDetail.pending, (state) => {
        state.dayDetail.loading = true;
        state.dayDetail.error = null;
      })
      .addCase(fetchDayDetail.fulfilled, (state, action) => {
        state.dayDetail.loading = false;
        state.dayDetail.scoreRows = action.payload?.scoreRows || [];
        state.dayDetail.matrixRows = action.payload?.matrixRows || [];
        state.dayDetail.dnDetail = action.payload?.dnDetail || null;
      })
      .addCase(fetchDayDetail.rejected, (state, action) => {
        state.dayDetail.loading = false;
        state.dayDetail.error = action.payload || action.error?.message || 'Failed to fetch day detail';
      })
      .addCase(fetchLeaderboardSeries.pending, (state) => {
        state.leaderboardSeriesLoading = true;
        state.leaderboardSeriesError = null;
      })
      .addCase(fetchLeaderboardSeries.fulfilled, (state, action) => {
        state.leaderboardSeriesLoading = false;
        state.leaderboardSeriesData = action.payload;
        state.leaderboardSeriesError = null;
      })
      .addCase(fetchLeaderboardSeries.rejected, (state, action) => {
        state.leaderboardSeriesLoading = false;
        state.leaderboardSeriesError = action.payload || action.error?.message || 'Failed to fetch leaderboard series';
      })
      .addCase(fetchMarketHealthSeries.pending, (state) => {
        state.marketHealthSeriesLoading = true;
        state.marketHealthSeriesError = null;
      })
      .addCase(fetchMarketHealthSeries.fulfilled, (state, action) => {
        state.marketHealthSeriesLoading = false;
        state.marketHealthSeriesData = action.payload;
        state.marketHealthSeriesError = null;
      })
      .addCase(fetchMarketHealthSeries.rejected, (state, action) => {
        state.marketHealthSeriesLoading = false;
        state.marketHealthSeriesData = null;
        state.marketHealthSeriesError = action.payload;
      })
      .addCase(fetchLeadtimeSeries.pending, (state) => {
        state.leadtimeSeriesLoading = true;
        state.leadtimeSeriesError = null;
      })
      .addCase(fetchLeadtimeSeries.fulfilled, (state, action) => {
        state.leadtimeSeriesLoading = false;
        state.leadtimeSeriesData = action.payload;
        state.leadtimeSeriesError = null;
      })
      .addCase(fetchLeadtimeSeries.rejected, (state, action) => {
        state.leadtimeSeriesLoading = false;
        state.leadtimeSeriesData = null;
        state.leadtimeSeriesError = action.payload || action.error?.message || 'Failed to fetch leadtime series';
      });
  }
});

export const {
  setSelectedMetric,
  setSelectedWindow,
  setPerfData,
  clearReportsState,
  clearMonthlyStats,
  clearMonthOverMonthStats,
  setSelectedReportHorizon,
  clearHorizonOverview,
  setSelectedGateHour,
  clearGateHourDetail,
  clearSubmissionHeatmap,
  openDayDetail,
  closeDayDetail,
  setDayDetailHorizon,
  setDayDetailTargetDay,
} = reportsSlice.actions;
export default reportsSlice.reducer;

// Selectors
export const selectReportsPerfData = (state) => state.reports.perfData;
export const selectReportsMetrics = (state) => state.reports.metrics;
export const selectReportsSessions = (state) => state.reports.sessions;
export const selectSelectedMetric = (state) => state.reports.selectedMetric;
export const selectSelectedWindow = (state) => state.reports.selectedWindow;
export const selectReportsLoading = (state) => state.reports.loading;
export const selectReportsError = (state) => state.reports.error;
// Monthly stats selectors
export const selectMonthlyStatsData = (state) => state.reports.monthlyStatsData;
export const selectMonthlyStatsLoading = (state) => state.reports.monthlyStatsLoading;
export const selectMonthlyStatsPreviousData = (state) => state.reports.monthlyStatsPreviousData;
export const selectMonthlyStatsError = (state) => state.reports.monthlyStatsError;
export const selectQualificationStatsData = (state) => state.reports.qualificationStatsData;
export const selectQualificationStatsLoading = (state) => state.reports.qualificationStatsLoading;
// Active users selectors
export const selectActiveUsers = (state) => state.reports.activeUsers;
export const selectActiveUsersLoading = (state) => state.reports.activeUsersLoading;
export const selectActiveUsersError = (state) => state.reports.activeUsersError;
// Month-over-month selectors
export const selectMonthOverMonthData = (state) => state.reports.monthOverMonthData;
export const selectMonthOverMonthLoading = (state) => state.reports.monthOverMonthLoading;
export const selectMonthOverMonthError = (state) => state.reports.monthOverMonthError;
// Horizon overview selectors
export const selectHorizonOverviewData = (state) => state.reports.horizonOverviewData;
export const selectHorizonOverviewLoading = (state) => state.reports.horizonOverviewLoading;
export const selectHorizonOverviewError = (state) => state.reports.horizonOverviewError;
export const selectSelectedReportHorizon = (state) => state.reports.selectedReportHorizon;
// Gate closure selectors
export const selectGateClosureData = (state) => state.reports.gateClosureData;
export const selectGateClosureLoading = (state) => state.reports.gateClosureLoading;
export const selectGateClosureError = (state) => state.reports.gateClosureError;
// Intraday gate hour drill-down selectors
export const selectGateHourDetailData = (state) => state.reports.gateHourDetailData;
export const selectGateHourDetailLoading = (state) => state.reports.gateHourDetailLoading;
export const selectGateHourDetailError = (state) => state.reports.gateHourDetailError;
export const selectSelectedGateHour = (state) => state.reports.selectedGateHour;
// Submission heatmap selectors
export const selectSubmissionHeatmapData = (state) => state.reports.submissionHeatmapData;
export const selectSubmissionHeatmapLoading = (state) => state.reports.submissionHeatmapLoading;
export const selectSubmissionHeatmapError = (state) => state.reports.submissionHeatmapError;
// Day-detail drawer selectors
export const selectDayDetail = (state) => state.reports.dayDetail;
// Trends — leaderboard series selectors
export const selectLeaderboardSeriesData = (state) => state.reports.leaderboardSeriesData;
export const selectLeaderboardSeriesLoading = (state) => state.reports.leaderboardSeriesLoading;
export const selectLeaderboardSeriesError = (state) => state.reports.leaderboardSeriesError;
// Trends — market-health series selectors
export const selectMarketHealthSeriesData = (state) => state.reports.marketHealthSeriesData;
export const selectMarketHealthSeriesLoading = (state) => state.reports.marketHealthSeriesLoading;
export const selectMarketHealthSeriesError = (state) => state.reports.marketHealthSeriesError;
// Overview — leadtime series selectors
export const selectLeadtimeSeriesData = (state) => state.reports.leadtimeSeriesData;
export const selectLeadtimeSeriesLoading = (state) => state.reports.leadtimeSeriesLoading;
export const selectLeadtimeSeriesError = (state) => state.reports.leadtimeSeriesError;

// Small helpers to derive ranks by metric (client-side) - optional convenience selector
export const selectRanksByMetric = createSelector(
  [selectReportsPerfData, selectReportsMetrics, selectReportsSessions],
  (perf, metrics, sessions) => {
    const out = {};
    metrics.forEach((m) => {
      out[m] = sessions.map((sid) => {
        const match = perf.find((r) => r.session_id === sid && r.metric === m);
        return match && typeof match.rank === 'number' ? match.rank : null;
      });
    });
    return out;
  }
);
